import { useEffect, useState, type CSSProperties } from "react";
import { GripVertical, Languages, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type PriceTier = {
  up_to: number | "inf" | null;
  unit_amount: number | null;
  unit_amount_decimal: string | null;
  flat_amount: number | null;
  flat_amount_decimal: string | null;
};

type PriceIndustry = "accounting" | "business";

type PriceVariant = {
  id: string;
  stripePriceId: string;
  unitAmount: number | null;
  currency: string | null;
  recurringInterval: string | null;
  recurringIntervalCount: number | null;
  isDefault: boolean | null;
  priceType: string | null;
  billingScheme: string | null;
  usageType: string | null;
  tiersMode: string | null;
  tiers: Array<Record<string, unknown>> | null;
  industry: PriceIndustry | null;
};

const INDUSTRY_BADGE: Record<PriceIndustry, { label: string; tone: string }> = {
  accounting: { label: "Accounting", tone: "border-blue-300 bg-blue-50 text-blue-700" },
  business: { label: "Business", tone: "border-amber-300 bg-amber-50 text-amber-800" },
};

function readTier(raw: Record<string, unknown>): PriceTier {
  const num = (v: unknown): number | null =>
    typeof v === "number" ? v : v == null ? null : Number(v);
  const upToRaw = raw.up_to;
  let upTo: number | "inf" | null = null;
  if (upToRaw === null || upToRaw === undefined) upTo = "inf";
  else if (typeof upToRaw === "string") upTo = upToRaw === "inf" ? "inf" : Number(upToRaw);
  else if (typeof upToRaw === "number") upTo = upToRaw;
  return {
    up_to: upTo,
    unit_amount: num(raw.unit_amount),
    unit_amount_decimal:
      typeof raw.unit_amount_decimal === "string" ? raw.unit_amount_decimal : null,
    flat_amount: num(raw.flat_amount),
    flat_amount_decimal:
      typeof raw.flat_amount_decimal === "string" ? raw.flat_amount_decimal : null,
  };
}

function tierUnitCents(tier: PriceTier): number | null {
  if (tier.unit_amount != null) return tier.unit_amount;
  if (tier.unit_amount_decimal != null) {
    const v = Number(tier.unit_amount_decimal);
    return Number.isFinite(v) ? v : null;
  }
  return null;
}

function formatTierUnit(tier: PriceTier, currency: string | null): string {
  const cents = tierUnitCents(tier);
  if (cents == null || cents === 0) return "—";
  if (cents < 100 && cents > 0) {
    return `${(cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: currency ?? "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })}`;
  }
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: currency ?? "EUR",
    minimumFractionDigits: 0,
  });
}

type ProductWithPrices = {
  id: string;
  stripeProductId: string;
  name: string;
  nameLt: string | null;
  nameEn: string | null;
  descriptionLt: string | null;
  descriptionEn: string | null;
  highlightLt: string | null;
  highlightEn: string | null;
  unitNameLt: string | null;
  unitNameEn: string | null;
  pricingUnitLt: string | null;
  pricingUnitEn: string | null;
  type: string | null;
  category: string | null;
  subCategory: string | null;
  isDefault: boolean;
  isEnabled: boolean;
  defaultPriceCents: number | null;
  defaultQty: number | null;
  currency: string | null;
  recurringInterval: string | null;
  sortOrder: number;
  syncedAt: Date | string;
  prices: PriceVariant[];
};

function formatAmount(cents: number | null, currency: string | null): string {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: currency ?? "EUR",
    minimumFractionDigits: 0,
  });
}

function intervalLabel(interval: string | null, intervalCount: number | null): string {
  if (!interval) return "one-time";
  const count = intervalCount && intervalCount > 1 ? `${intervalCount} ` : "";
  if (interval === "month") return `${count}mo`;
  if (interval === "year") return `${count}yr`;
  return interval;
}

function BillingTypeBadge({
  usageType,
  priceType,
}: {
  usageType: string | null;
  priceType: string | null;
}) {
  if (priceType === "one_time") {
    return (
      <Badge variant="outline" className="text-xs border-purple-400 text-purple-700">
        One-time fee
      </Badge>
    );
  }
  if (usageType === "metered") {
    return <Badge variant="outline" className="text-xs border-amber-400 text-amber-700">Usage-based</Badge>;
  }
  return <Badge variant="outline" className="text-xs">Fixed</Badge>;
}

function firstFreeTierUpTo(tiers: PriceTier[]): number | null {
  // A free starter tier is one with no per-unit and no flat charge and a
  // finite cap — e.g. "first 10 free, then €X/unit". Used to surface the
  // included quantity on the admin price row so it doesn't hide in a tooltip.
  const free = tiers.find((t) => {
    const u = tierUnitCents(t);
    const flat = t.flat_amount ?? null;
    const unitFree = u == null || u === 0;
    const flatFree = flat == null || flat === 0;
    return unitFree && flatFree;
  });
  if (!free) return null;
  if (free.up_to === "inf" || free.up_to == null) return null;
  return free.up_to;
}

function TieredPriceDisplay({
  price,
  unitName,
}: {
  price: PriceVariant;
  unitName?: string | null;
}) {
  const tiers = (price.tiers ?? []).map(readTier);
  const currency = price.currency;
  const firstPaid = tiers.find((t) => {
    const u = tierUnitCents(t);
    return (u != null && u > 0) || (t.flat_amount != null && t.flat_amount > 0);
  });
  const headlineUnit = firstPaid ? tierUnitCents(firstPaid) : null;
  const headline =
    headlineUnit != null && headlineUnit > 0
      ? formatTierUnit(firstPaid!, currency)
      : firstPaid?.flat_amount != null && firstPaid.flat_amount > 0
        ? formatAmount(firstPaid.flat_amount, currency)
        : null;
  const freeUpTo = firstFreeTierUpTo(tiers);
  const freeLabel =
    freeUpTo != null
      ? `first ${freeUpTo.toLocaleString("en-US")}${unitName ? ` ${unitName}` : ""} free`
      : null;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="font-mono text-xs font-medium underline decoration-dotted underline-offset-2 cursor-help">
            {headline ? `from ${headline}` : "Tiered"}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">
          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {price.tiersMode ?? "tiered"} pricing
            </div>
            {tiers.length === 0 && (
              <div className="text-xs">No tier data available.</div>
            )}
            {tiers.map((t, i) => {
              const upTo =
                t.up_to === "inf" || t.up_to == null
                  ? "∞"
                  : t.up_to.toLocaleString("en-US");
              const unit = tierUnitCents(t);
              const parts: string[] = [];
              if (unit != null && unit > 0) {
                parts.push(`${formatTierUnit(t, currency)} / unit`);
              }
              if (t.flat_amount != null && t.flat_amount > 0) {
                parts.push(`${formatAmount(t.flat_amount, currency)} flat`);
              }
              if (parts.length === 0) parts.push("—");
              return (
                <div key={i} className="flex justify-between gap-3 text-xs font-mono">
                  <span className="text-muted-foreground">up to {upTo}</span>
                  <span>{parts.join(" + ")}</span>
                </div>
              );
            })}
          </div>
        </TooltipContent>
      </Tooltip>
      {freeLabel && (
        <Badge
          variant="outline"
          className="text-[10px] font-mono border-emerald-300 bg-emerald-50 text-emerald-700"
        >
          {freeLabel}
        </Badge>
      )}
    </TooltipProvider>
  );
}

function PriceVariantList({
  prices,
  unitName,
}: {
  prices: PriceVariant[];
  unitName?: string | null;
}) {
  const industryPrices = prices.filter(
    (p) => p.industry === "accounting" || p.industry === "business"
  );
  if (industryPrices.length === 0)
    return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col gap-1">
      {industryPrices.map((p) => {
        const isTiered =
          p.billingScheme === "tiered" || (p.tiers != null && p.tiers.length > 0);
        const industry = p.industry as PriceIndustry;
        const badge = INDUSTRY_BADGE[industry];
        return (
          <div key={p.id} className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className={cn("text-xs", badge.tone)}>
              {badge.label}
            </Badge>
            {isTiered ? (
              <TieredPriceDisplay price={p} unitName={unitName} />
            ) : (
              <span className="font-mono text-xs font-medium">
                {formatAmount(p.unitAmount, p.currency)}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              / {intervalLabel(p.recurringInterval, p.recurringIntervalCount)}
            </span>
            <BillingTypeBadge usageType={p.usageType} priceType={p.priceType} />
          </div>
        );
      })}
    </div>
  );
}

export function ProductsPage() {
  const utils = trpc.useUtils();

  const list = trpc.products.listWithPrices.useQuery();
  const sync = trpc.products.syncFromStripe.useMutation({
    onSuccess: (data) => {
      utils.products.listWithPrices.invalidate();
      toast.success(data.message ?? "Products synced");
    },
    onError: (e) => toast.error(e.message ?? "Sync error"),
  });

  const updateCategory = trpc.products.updateCategory.useMutation({
    onSuccess: () => {
      utils.products.listWithPrices.invalidate();
      toast.success("Category updated");
    },
    onError: (e) => toast.error(e.message ?? "Failed to update category"),
  });

  const updateSubCategory = trpc.products.updateSubCategory.useMutation({
    onSuccess: () => {
      utils.products.listWithPrices.invalidate();
      toast.success("Sub-category updated");
    },
    onError: (e) => toast.error(e.message ?? "Failed to update sub-category"),
  });

  const updateIsEnabled = trpc.products.updateIsEnabled.useMutation({
    onSuccess: () => {
      utils.products.listWithPrices.invalidate();
      toast.success("Visibility updated");
    },
    onError: (e) => toast.error(e.message ?? "Failed to update visibility"),
  });

  const updateDefaultQty = trpc.products.updateDefaultQty.useMutation({
    onSuccess: () => {
      utils.products.listWithPrices.invalidate();
      toast.success("Default quantity updated");
    },
    onError: (e) => toast.error(e.message ?? "Failed to update default quantity"),
  });

  const updateTranslations = trpc.products.updateTranslations.useMutation({
    onSuccess: () => {
      utils.products.listWithPrices.invalidate();
      toast.success("Translation saved");
    },
    onError: (e) => toast.error(e.message ?? "Failed to save translation"),
  });

  const translateOne = trpc.products.translateWithAi.useMutation({
    onSuccess: (res) => {
      utils.products.listWithPrices.invalidate();
      if (res.ok) toast.success("AI translation generated");
      else toast.error(res.message ?? "Failed to translate");
    },
    onError: (e) => toast.error(e.message ?? "Failed to translate"),
  });

  const translateAllMissing = trpc.products.translateAllMissing.useMutation({
    onSuccess: (res) => {
      utils.products.listWithPrices.invalidate();
      toast.success(res.message ?? `Translated ${res.translated}`);
    },
    onError: (e) => toast.error(e.message ?? "Bulk translation failed"),
  });

  const reorder = trpc.products.reorder.useMutation({
    onError: (e) => {
      utils.products.listWithPrices.invalidate();
      toast.error(e.message ?? "Failed to save order");
    },
    onSuccess: () => {
      utils.products.listWithPrices.invalidate();
    },
  });

  const handleReorderSection = (sectionProducts: ProductWithPrices[], orderedIds: string[]) => {
    const sortValues = sectionProducts.map((p) => p.sortOrder).sort((a, b) => a - b);
    const items = orderedIds.map((id, idx) => ({ id, sortOrder: sortValues[idx] ?? idx }));
    const updates = new Map(items.map((it) => [it.id, it.sortOrder]));

    utils.products.listWithPrices.setData(undefined, (prev) => {
      if (!prev) return prev;
      const next = prev.map((p) =>
        updates.has(p.id) ? { ...p, sortOrder: updates.get(p.id)! } : p
      );
      return next.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    });

    reorder.mutate({ items });
  };

  const products = (list.data ?? []) as ProductWithPrices[];
  const mainProducts = products.filter((p) => p.category === "main");
  const extraProducts = products.filter((p) => p.category === "extra");
  const accountingSystemProducts = products.filter(
    (p) => p.category === "accounting_system"
  );
  const otherProducts = products.filter(
    (p) =>
      p.category !== "main" &&
      p.category !== "extra" &&
      p.category !== "accounting_system"
  );

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 py-6 sm:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground">
            Products and prices synced from Stripe. Assign categories and sub-categories for the Quote Builder.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
          <Button
            variant="outline"
            onClick={() => translateAllMissing.mutate()}
            disabled={translateAllMissing.isPending}
            className="w-full sm:w-auto"
          >
            {translateAllMissing.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {translateAllMissing.isPending ? "Translating..." : "Translate all missing"}
          </Button>
          <Button onClick={() => sync.mutate()} disabled={sync.isPending} className="w-full sm:w-auto">
            <RefreshCw className={cn("h-4 w-4", sync.isPending && "animate-spin")} />
            {sync.isPending ? "Syncing..." : "Sync with Stripe"}
          </Button>
        </div>
      </div>

      {list.isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {!list.isLoading && products.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-center">
            <p className="text-muted-foreground">No products.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure <code>STRIPE_SECRET_KEY</code> and click "Sync".
            </p>
            <Button className="mt-4" onClick={() => sync.mutate()} disabled={sync.isPending}>
              <RefreshCw className={cn("h-4 w-4", sync.isPending && "animate-spin")} />
              Sync with Stripe
            </Button>
          </CardContent>
        </Card>
      )}

      {products.length > 0 && (
        <div className="space-y-8">
          {mainProducts.length > 0 && (
            <ProductTable
              title="Core plans"
              products={mainProducts}
              onCategoryChange={(id, cat) => updateCategory.mutate({ id, category: cat })}
              onSubCategoryChange={(id, sub) => updateSubCategory.mutate({ id, subCategory: sub })}
              onIsEnabledChange={(id, val) => updateIsEnabled.mutate({ id, isEnabled: val })}
              onDefaultQtyChange={(id, qty) => updateDefaultQty.mutate({ id, defaultQty: qty })}
              onSaveTranslations={(id, t) => updateTranslations.mutate({ id, ...t })}
              onTranslateWithAi={(id) => translateOne.mutate({ id })}
              translatingId={translateOne.isPending ? translateOne.variables?.id ?? null : null}
              onReorder={handleReorderSection}
            />
          )}
          {extraProducts.length > 0 && (
            <ProductTable
              title="Extra modules"
              products={extraProducts}
              onCategoryChange={(id, cat) => updateCategory.mutate({ id, category: cat })}
              onSubCategoryChange={(id, sub) => updateSubCategory.mutate({ id, subCategory: sub })}
              onIsEnabledChange={(id, val) => updateIsEnabled.mutate({ id, isEnabled: val })}
              onDefaultQtyChange={(id, qty) => updateDefaultQty.mutate({ id, defaultQty: qty })}
              onSaveTranslations={(id, t) => updateTranslations.mutate({ id, ...t })}
              onTranslateWithAi={(id) => translateOne.mutate({ id })}
              translatingId={translateOne.isPending ? translateOne.variables?.id ?? null : null}
              onReorder={handleReorderSection}
            />
          )}
          {accountingSystemProducts.length > 0 && (
            <ProductTable
              title="Accounting system"
              products={accountingSystemProducts}
              onCategoryChange={(id, cat) => updateCategory.mutate({ id, category: cat })}
              onSubCategoryChange={(id, sub) => updateSubCategory.mutate({ id, subCategory: sub })}
              onIsEnabledChange={(id, val) => updateIsEnabled.mutate({ id, isEnabled: val })}
              onDefaultQtyChange={(id, qty) => updateDefaultQty.mutate({ id, defaultQty: qty })}
              onSaveTranslations={(id, t) => updateTranslations.mutate({ id, ...t })}
              onTranslateWithAi={(id) => translateOne.mutate({ id })}
              translatingId={translateOne.isPending ? translateOne.variables?.id ?? null : null}
              onReorder={handleReorderSection}
            />
          )}
          {otherProducts.length > 0 && (
            <ProductTable
              title="Other products"
              products={otherProducts}
              onCategoryChange={(id, cat) => updateCategory.mutate({ id, category: cat })}
              onSubCategoryChange={(id, sub) => updateSubCategory.mutate({ id, subCategory: sub })}
              onIsEnabledChange={(id, val) => updateIsEnabled.mutate({ id, isEnabled: val })}
              onDefaultQtyChange={(id, qty) => updateDefaultQty.mutate({ id, defaultQty: qty })}
              onSaveTranslations={(id, t) => updateTranslations.mutate({ id, ...t })}
              onTranslateWithAi={(id) => translateOne.mutate({ id })}
              translatingId={translateOne.isPending ? translateOne.variables?.id ?? null : null}
              onReorder={handleReorderSection}
            />
          )}
        </div>
      )}
    </div>
  );
}

type TranslationPatch = {
  nameLt?: string | null;
  nameEn?: string | null;
  descriptionLt?: string | null;
  descriptionEn?: string | null;
  highlightLt?: string | null;
  highlightEn?: string | null;
  unitNameLt?: string | null;
  unitNameEn?: string | null;
  pricingUnitLt?: string | null;
  pricingUnitEn?: string | null;
};

type SubCategory = "seats" | "core" | "hub" | "onboarding";

function ProductTable({
  title,
  products,
  onCategoryChange,
  onSubCategoryChange,
  onIsEnabledChange,
  onDefaultQtyChange,
  onSaveTranslations,
  onTranslateWithAi,
  translatingId,
  onReorder,
}: {
  title: string;
  products: ProductWithPrices[];
  onCategoryChange: (id: string, cat: "main" | "extra" | "accounting_system") => void;
  onSubCategoryChange: (id: string, sub: SubCategory | null) => void;
  onIsEnabledChange: (id: string, val: boolean) => void;
  onDefaultQtyChange: (id: string, qty: number | null) => void;
  onSaveTranslations: (id: string, patch: TranslationPatch) => void;
  onTranslateWithAi: (id: string) => void;
  translatingId: string | null;
  onReorder: (sectionProducts: ProductWithPrices[], orderedIds: string[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = products.findIndex((p) => p.id === active.id);
    const newIndex = products.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(products, oldIndex, newIndex);
    onReorder(
      products,
      next.map((p) => p.id)
    );
  };

  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold">{title}</h2>
      <Card className="overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Table className="min-w-[820px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Product</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Prices</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Sub-category</TableHead>
                <TableHead>Default qty</TableHead>
                <TableHead>Visible</TableHead>
                <TableHead>Synced</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableContext
                items={products.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                {products.map((p) => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    onCategoryChange={onCategoryChange}
                    onSubCategoryChange={onSubCategoryChange}
                    onIsEnabledChange={onIsEnabledChange}
                    onDefaultQtyChange={onDefaultQtyChange}
                    onSaveTranslations={onSaveTranslations}
                    onTranslateWithAi={onTranslateWithAi}
                    isTranslating={translatingId === p.id}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </DndContext>
      </Card>
    </div>
  );
}

function ProductRow({
  product: p,
  onCategoryChange,
  onSubCategoryChange,
  onIsEnabledChange,
  onDefaultQtyChange,
  onSaveTranslations,
  onTranslateWithAi,
  isTranslating,
}: {
  product: ProductWithPrices;
  onCategoryChange: (id: string, cat: "main" | "extra" | "accounting_system") => void;
  onSubCategoryChange: (id: string, sub: SubCategory | null) => void;
  onIsEnabledChange: (id: string, val: boolean) => void;
  onDefaultQtyChange: (id: string, qty: number | null) => void;
  onSaveTranslations: (id: string, patch: TranslationPatch) => void;
  onTranslateWithAi: (id: string) => void;
  isTranslating: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [nameLt, setNameLt] = useState(p.nameLt ?? "");
  const [nameEn, setNameEn] = useState(p.nameEn ?? "");
  const [descLt, setDescLt] = useState(p.descriptionLt ?? "");
  const [descEn, setDescEn] = useState(p.descriptionEn ?? "");
  const [highlightLt, setHighlightLt] = useState(p.highlightLt ?? "");
  const [highlightEn, setHighlightEn] = useState(p.highlightEn ?? "");
  const [unitNameLt, setUnitNameLt] = useState(p.unitNameLt ?? "");
  const [unitNameEn, setUnitNameEn] = useState(p.unitNameEn ?? "");
  const [pricingUnitLt, setPricingUnitLt] = useState(p.pricingUnitLt ?? "");
  const [pricingUnitEn, setPricingUnitEn] = useState(p.pricingUnitEn ?? "");
  const [defaultQtyInput, setDefaultQtyInput] = useState(
    p.defaultQty == null ? "" : String(p.defaultQty),
  );

  const includedFromFreeTier = (() => {
    for (const price of p.prices) {
      const tiers = (price.tiers ?? []).map(readTier);
      const upTo = firstFreeTierUpTo(tiers);
      if (upTo != null) return upTo;
    }
    return null;
  })();
  const unitLabel = p.unitNameEn ?? p.unitNameLt ?? null;

  useEffect(() => {
    setDefaultQtyInput(p.defaultQty == null ? "" : String(p.defaultQty));
  }, [p.defaultQty]);

  const commitDefaultQty = () => {
    const trimmed = defaultQtyInput.trim();
    const next = trimmed === "" ? null : Number.parseInt(trimmed, 10);
    if (next !== null && (!Number.isFinite(next) || next < 0)) {
      setDefaultQtyInput(p.defaultQty == null ? "" : String(p.defaultQty));
      return;
    }
    if (next === (p.defaultQty ?? null)) return;
    onDefaultQtyChange(p.id, next);
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: p.id });
  const sortableStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    position: "relative",
    zIndex: isDragging ? 10 : undefined,
  };

  useEffect(() => {
    setNameLt(p.nameLt ?? "");
    setNameEn(p.nameEn ?? "");
    setDescLt(p.descriptionLt ?? "");
    setDescEn(p.descriptionEn ?? "");
    setHighlightLt(p.highlightLt ?? "");
    setHighlightEn(p.highlightEn ?? "");
    setUnitNameLt(p.unitNameLt ?? "");
    setUnitNameEn(p.unitNameEn ?? "");
    setPricingUnitLt(p.pricingUnitLt ?? "");
    setPricingUnitEn(p.pricingUnitEn ?? "");
  }, [p.nameLt, p.nameEn, p.descriptionLt, p.descriptionEn, p.highlightLt, p.highlightEn, p.unitNameLt, p.unitNameEn, p.pricingUnitLt, p.pricingUnitEn]);

  const dirty =
    nameLt !== (p.nameLt ?? "") ||
    nameEn !== (p.nameEn ?? "") ||
    descLt !== (p.descriptionLt ?? "") ||
    descEn !== (p.descriptionEn ?? "") ||
    highlightLt !== (p.highlightLt ?? "") ||
    highlightEn !== (p.highlightEn ?? "") ||
    unitNameLt !== (p.unitNameLt ?? "") ||
    unitNameEn !== (p.unitNameEn ?? "") ||
    pricingUnitLt !== (p.pricingUnitLt ?? "") ||
    pricingUnitEn !== (p.pricingUnitEn ?? "");

  const missingTranslations =
    !p.nameLt || !p.nameEn || !p.descriptionLt || !p.descriptionEn;

  return (
    <TableRow ref={setNodeRef} style={sortableStyle}>
      <TableCell className="w-8 p-2 align-middle">
        <button
          type="button"
          className="flex h-8 w-6 cursor-grab touch-none items-center justify-center text-muted-foreground hover:text-foreground active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell>
        <div className="flex items-start gap-2">
          <div className="min-w-0">
            <div className="font-medium">{p.name}</div>
            <div className="text-xs text-muted-foreground">{p.stripeProductId}</div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 shrink-0"
                title="Translations"
                aria-label="Translations"
              >
                <Languages
                  className={cn(
                    "h-4 w-4",
                    missingTranslations ? "text-amber-600" : "text-muted-foreground",
                  )}
                />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>Translations</DialogTitle>
                <DialogDescription>
                  Stripe name:{" "}
                  <span className="font-medium text-foreground">{p.name}</span>
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Name (LT)</label>
                  <Input
                    value={nameLt}
                    onChange={(e) => setNameLt(e.target.value)}
                    placeholder="e.g. Accounting Plus"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Name (EN)</label>
                  <Input
                    value={nameEn}
                    onChange={(e) => setNameEn(e.target.value)}
                    placeholder="e.g. Accounting Plus"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Description (LT)</label>
                  <Textarea
                    value={descLt}
                    onChange={(e) => setDescLt(e.target.value)}
                    placeholder="Short description for the proposal calculator"
                    rows={3}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Description (EN)</label>
                  <Textarea
                    value={descEn}
                    onChange={(e) => setDescEn(e.target.value)}
                    placeholder="Short description for the proposal calculator"
                    rows={3}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    Highlight (LT) — emphasised callout below the description
                  </label>
                  <Textarea
                    value={highlightLt}
                    onChange={(e) => setHighlightLt(e.target.value)}
                    placeholder="e.g. Įskaičiuota 100 GB saugyklos"
                    rows={2}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    Highlight (EN) — emphasised callout below the description
                  </label>
                  <Textarea
                    value={highlightEn}
                    onChange={(e) => setHighlightEn(e.target.value)}
                    placeholder="e.g. Includes 100 GB storage"
                    rows={2}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    Unit name (LT) — next to quantity input
                  </label>
                  <Input
                    value={unitNameLt}
                    onChange={(e) => setUnitNameLt(e.target.value)}
                    placeholder="e.g. vnt., doc., naudotojai"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    Unit name (EN) — next to quantity input
                  </label>
                  <Input
                    value={unitNameEn}
                    onChange={(e) => setUnitNameEn(e.target.value)}
                    placeholder="e.g. pcs, doc, users"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    Pricing unit (LT) — price suffix on the calculator
                  </label>
                  <Input
                    value={pricingUnitLt}
                    onChange={(e) => setPricingUnitLt(e.target.value)}
                    placeholder="e.g. sąskaitos eilutė, kvitas, mėn."
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    Pricing unit (EN) — price suffix on the calculator
                  </label>
                  <Input
                    value={pricingUnitEn}
                    onChange={(e) => setPricingUnitEn(e.target.value)}
                    placeholder="e.g. invoice line, receipt, mo."
                    className="h-8 text-sm"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onTranslateWithAi(p.id)}
                  disabled={isTranslating}
                >
                  {isTranslating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  Translate with AI
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    onSaveTranslations(p.id, {
                      nameLt: nameLt.trim() || null,
                      nameEn: nameEn.trim() || null,
                      descriptionLt: descLt.trim() || null,
                      descriptionEn: descEn.trim() || null,
                      highlightLt: highlightLt.trim() || null,
                      highlightEn: highlightEn.trim() || null,
                      unitNameLt: unitNameLt.trim() || null,
                      unitNameEn: unitNameEn.trim() || null,
                      pricingUnitLt: pricingUnitLt.trim() || null,
                      pricingUnitEn: pricingUnitEn.trim() || null,
                    });
                    setOpen(false);
                  }}
                  disabled={!dirty}
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </TableCell>
      <TableCell>
        {p.type && (
          <Badge variant="outline" className="text-xs">
            {p.type}
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <PriceVariantList
          prices={p.prices}
          unitName={p.unitNameEn ?? p.unitNameLt ?? null}
        />
      </TableCell>
      <TableCell>
        <Select
          value={p.category ?? "extra"}
          onValueChange={(v) =>
            onCategoryChange(p.id, v as "main" | "extra" | "accounting_system")
          }
        >
          <SelectTrigger className="h-7 w-36 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="main">Main</SelectItem>
            <SelectItem value="extra">Extra</SelectItem>
            <SelectItem value="accounting_system">Accounting system</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Select
          value={p.subCategory ?? "none"}
          onValueChange={(v) =>
            onSubCategoryChange(p.id, v === "none" ? null : (v as SubCategory))
          }
        >
          <SelectTrigger className="h-7 w-24 text-xs">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            <SelectItem value="seats">Seats</SelectItem>
            <SelectItem value="core">Storage</SelectItem>
            <SelectItem value="hub">Hub</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={defaultQtyInput}
            onChange={(e) => setDefaultQtyInput(e.target.value)}
            onBlur={commitDefaultQty}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            placeholder="—"
            className="h-7 w-20 text-xs"
          />
          {includedFromFreeTier != null && (
            <span className="font-mono text-[10px] text-emerald-700">
              {includedFromFreeTier.toLocaleString("en-US")}
              {unitLabel ? ` ${unitLabel}` : ""} free
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch
            checked={p.isEnabled}
            onCheckedChange={(v) => onIsEnabledChange(p.id, v)}
            className="scale-75"
          />
          {!p.isEnabled && <span className="text-xs text-muted-foreground">Hidden</span>}
        </div>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {new Date(p.syncedAt).toLocaleDateString("en-US")}
      </TableCell>
    </TableRow>
  );
}
